import { useState, useCallback } from 'react';
import { saveCurrentState, loadCurrentState, NewPreemieState } from '../storage';

type SectionKey = 'medications' | 'roomEquipment' | 'ventilationEquipment' | 'intubationEquipment' | 'departmentAdmission' | 'parents';

/**
 * Custom hook to manage checkbox state with automatic timestamp tracking
 *
 * @param sectionKey - The section key in storage (e.g., 'medications')
 * @param checkboxKey - The specific checkbox key (e.g., 'prenatalSteroids')
 * @param initialValue - Initial boolean value from storage
 * @returns [checked, setChecked] tuple similar to useState
 */
export const useCheckboxWithTimestamp = (
  sectionKey: SectionKey,
  checkboxKey: string,
  initialValue: boolean
): [boolean, (checked: boolean) => void] => {
  const [checked, setCheckedState] = useState(initialValue);

  const setChecked = useCallback((newChecked: boolean) => {
    setCheckedState(newChecked);

    const currentState = loadCurrentState() || {};
    const timestampKey = `${sectionKey}Timestamps` as keyof NewPreemieState;

    // Update the boolean value
    const updatedSection = {
      ...currentState[sectionKey],
      [checkboxKey]: newChecked
    };

    // Update the timestamp
    const updatedTimestamps = {
      ...currentState[timestampKey],
      [checkboxKey]: newChecked ? new Date().toISOString() : undefined
    };

    // Save both the boolean and timestamp
    saveCurrentState({
      [sectionKey]: updatedSection,
      [timestampKey]: updatedTimestamps
    });
  }, [sectionKey, checkboxKey]);

  return [checked, setChecked];
};
